import { createFileRoute } from '@tanstack/react-router';

import { useTimeZone } from '@/auth/useTimeZone.js';
import { CoachSessionsTableColumnIdSchema } from '@/components/session/sessions-table-columns.js';
import { TeacherSessionDateDivider } from '@/components/session/teacher-session-date-divider.js';
import { EntityTableGroupBy } from '@/components/table/entity-table.js';
import { coachSessionListSearchSchema } from '@/lib/my-classes-utils.js';
import { validateRouteRole } from '@/lib/routing.js';
import { groupByStartDate, StartDateGroupKey } from '@/lib/table-grouping-utils.js';
import { isCoach, startOfDay } from '@airreading/common';
import { CoachPortalSession, CoachSessionRemarkV2 } from '@airreading/common/data';
import { useMemo } from 'react';
import { Temporal } from 'temporal-polyfill';
import { CoachSessionList } from './-components/coach-session-list.js';

export const Route = createFileRoute('/_layout/my-classes/upcoming')({
  beforeLoad: validateRouteRole(isCoach),
  component: MyUpcomingClassesList,
  validateSearch: coachSessionListSearchSchema,
});

function MyUpcomingClassesList() {
  const { timeZone } = useTimeZone();

  const sessionGroupBy = useMemo<EntityTableGroupBy<CoachPortalSession, StartDateGroupKey>>(
    () => ({
      getGroupKey: (row) => groupByStartDate(row.startInstant, timeZone),
      renderDivider: (groupKey, rowCount) => <TeacherSessionDateDivider groupKey={groupKey} rowCount={rowCount} />,
    }),
    [timeZone],
  );
  const zonedNow = Temporal.Now.zonedDateTimeISO(timeZone);
  const startOfToday = startOfDay(zonedNow.toInstant(), timeZone);

  const hiddenColumnIdsSchema = CoachSessionsTableColumnIdSchema.extract(['status', 'feedback']);
  const hideColumnIds = hiddenColumnIdsSchema.options;

  return (
    <CoachSessionList
      route={Route}
      title="Upcoming classes"
      startDate={startOfToday}
      hideColumnIds={hideColumnIds}
      defaultSessionRemarksV2={[
        CoachSessionRemarkV2.endEarly,
        CoachSessionRemarkV2.lateStart,
        CoachSessionRemarkV2.subClass,
        CoachSessionRemarkV2.subClassWithin12Hours,
      ]}
      from="upcoming"
      groupBy={sessionGroupBy}
    />
  );
}
